import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert, CircularProgress, Chip, ButtonGroup, Paper, IconButton, useMediaQuery, useTheme, FormControlLabel, Switch } from '@mui/material';
import { Stage, Layer, Circle, Text, Group, Line, Rect } from 'react-konva';
import { useAsientosRealtime } from "../../../hooks/useAsientosRealtime";
import { CloseOutlined, ShoppingCart, LocalOffer, ZoomIn, ZoomOut, RestartAlt } from '@mui/icons-material';
import { api } from '../../../services/api';
import { toast } from 'react-toastify';

const SelectUbicacionModal = ({
  open,
  onClose,
  tarifa,
  cantidadSeleccionada = 1,
  onConfirmar,
  seleccionActual,
  tarifasTicketsFiltrados
}) => {
  const [layout, setLayout] = useState(null);
  const [cantidad, setCantidad] = useState(cantidadSeleccionada || 1);
  const [asientosSeleccionados, setAsientosSeleccionados] = useState([]);
  const [mesasSeleccionadas, setMesasSeleccionadas] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isRealtimeEnabled, setIsRealtimeEnabled] = useState(true);

  const [escala, setEscala] = useState(1);
  const [posicion, setPosicion] = useState({ x: 0, y: 0 });

  const stageRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const totalEntradasAsignadas = Object.values(mesasSeleccionadas).reduce((sum, count) => sum + count, 0);

  const [mesaActiva, setMesaActiva] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [showPopover, setShowPopover] = useState(false);

  const screenHeight = window.innerHeight;
  const isSmallScreen = screenHeight < 700;

  const CANVAS_WIDTH = isMobile ? Math.min(window.innerWidth - 40, 380) : 700;
  const CANVAS_HEIGHT = isMobile ?
    Math.min(window.innerHeight * 0.6, isSmallScreen ? 300 : 400) :
    450;


  /* =============== SOCKET ======= */
  const { isConnected, addUpdateCallback, removeUpdateCallback } = useAsientosRealtime(
    tarifa?.idevento,
    open && isRealtimeEnabled
  );

  const limpiarSeleccionesInvalidas = useCallback((layoutData) => {
    if (!layoutData) return;

    // limpiar asientos seleccionados que ya no están disponibles
    setAsientosSeleccionados(prev => {
      const asientosValidos = prev.filter(idAsiento => {
        // buscar el asiento en el layout
        let asientoEncontrado = null;

        // buscar en asientos individuales
        if (layoutData.asientos) {
          asientoEncontrado = layoutData.asientos.find(a => a.id === idAsiento);
        }

        // buscar en mesas
        if (!asientoEncontrado && layoutData.mesas) {
          for (const mesa of layoutData.mesas) {
            if (mesa.asientos) {
              asientoEncontrado = mesa.asientos.find(a => a.id === idAsiento);
              if (asientoEncontrado) break;
            }
          }
        }

        // solo mantener si esta disponible
        return asientoEncontrado && asientoEncontrado.disponible && !asientoEncontrado.ocupado;
      });

      if (asientosValidos.length !== prev.length) {
        console.log(`🧹 Limpiados ${prev.length - asientosValidos.length} asientos no disponibles`);
      }

      return asientosValidos;
    });

    // limpiar mesas seleccionadas que ya no tienen disponibilidad
    setMesasSeleccionadas(prev => {
      const mesasValidas = {};

      Object.entries(prev).forEach(([mesaId, cantidad]) => {
        const mesa = layoutData.mesas?.find(m => m.id == mesaId);

        if (mesa && mesa.mesaDisponible && mesa.asientos_disponibles > 0) {
          // ajustar la cantidad si es mayor a la disponibilidad actual
          mesasValidas[mesaId] = Math.min(cantidad, mesa.asientos_disponibles);
        }
      });

      const mesasLimpiadas = Object.keys(prev).length - Object.keys(mesasValidas).length;
      const cantidadesAjustadas = Object.entries(prev).filter(([id, cant]) =>
        mesasValidas[id] && mesasValidas[id] !== cant).length;

      if (mesasLimpiadas > 0 || cantidadesAjustadas > 0) {
        console.log(`🧹 Limpiadas ${mesasLimpiadas} mesas no disponibles, ${cantidadesAjustadas} cantidades ajustadas`);
      }

      return mesasValidas;
    });
  }, []);

  // actualizacion de asientos en tiempo real
  const handleAsientoUpdate = useCallback((data) => {

    setLayout(prevLayout => {
      if (!prevLayout) return prevLayout;

      const newLayout = { ...prevLayout };

      // asientos indiv
      if (newLayout.asientos) {
        newLayout.asientos = newLayout.asientos.map(asiento =>
          asiento.id === data.asientoId
            ? { ...asiento, ocupado: data.ocupado, disponible: !data.ocupado }
            : asiento
        );
      }

      // asientos mesas
      if (newLayout.mesas) {
        newLayout.mesas = newLayout.mesas.map(mesa => ({
          ...mesa,
          asientos: mesa.asientos?.map(asiento =>
            asiento.id === data.asientoId
              ? { ...asiento, ocupado: data.ocupado, disponible: !data.ocupado }
              : asiento
          )
        }));
      }
      setTimeout(() => {
        limpiarSeleccionesInvalidas(newLayout);
      }, 100);

      return newLayout;
    });

    // si el asiento recien ocupado esta seleccionado, deselecciono
    if (data.ocupado) {
      setAsientosSeleccionados(prev => {
        const nuevaSeleccion = prev.filter(id => id !== data.asientoId);
        if (nuevaSeleccion.length !== prev.length) {
          toast.warning('Un asiento que habías seleccionado acaba de ser ocupado', {
            position: "top-right",
            autoClose: 5000,
          });
        }
        return nuevaSeleccion;
      });
    }
  }, []);

  // callback cada que se abre el modal
  useEffect(() => {
    if (open && isConnected) {
      addUpdateCallback(handleAsientoUpdate);
      return () => removeUpdateCallback(handleAsientoUpdate);
    }
  }, [open, isConnected, handleAsientoUpdate, addUpdateCallback, removeUpdateCallback]);

  const getNumeroAsiento = (asiento) => {
    let label = asiento.numero || asiento.asiento || asiento.id;
    if (typeof label === 'string') {
      const match = label.match(/(\d+)/);
      if (match) {
        label = match[1];
      }
    }
    return label;
  };

  //calcular cual es el centro del contenido para centrar el canvas
  const calcularBoundingBox = () => {
    if (!layout) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0, centerX: 0, centerY: 0 };
    }

    const CANVAS_PADDING = 50;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let hasPrimary = false; // mesas/asientos
    let hasAnything = false; // primary o decorativos

    const expandBox = (x1, y1, x2, y2) => {
      hasAnything = true;
      minX = Math.min(minX, x1);
      minY = Math.min(minY, y1);
      maxX = Math.max(maxX, x2);
      maxY = Math.max(maxY, y2);
    };

    if (layout.mesas?.length) {
      layout.mesas.forEach(mesa => {
        if (mesa.coordX == null || mesa.coordY == null) return;

        hasPrimary = true;
        const w = mesa.ancho ?? 50;
        const h = mesa.alto ?? 50;

        expandBox(mesa.coordX, mesa.coordY, mesa.coordX + w, mesa.coordY + h);
      });
    }

    if (layout.asientos?.length) {
      layout.asientos.forEach(asiento => {
        if (asiento.coordX == null || asiento.coordY == null) return;

        hasPrimary = true;
        const asientoSize = isMobile ? 30 : 40;

        expandBox(
          asiento.coordX - asientoSize / 2,
          asiento.coordY - asientoSize / 2,
          asiento.coordX + asientoSize / 2,
          asiento.coordY + asientoSize / 2
        );
      });
    }

    if (!hasPrimary) {
      if (layout.decorativos?.length) {
        const MAX_RECT_WIDTH = 3000;
        const MAX_RECT_HEIGHT = 1500;

        layout.decorativos.forEach(decorativo => {
          if (decorativo.coordX == null || decorativo.coordY == null) return;

          // ignorar rectangulos gigantes (calle)
          if (
            decorativo.tipo === 'rectangulo' &&
            ((decorativo.ancho || 0) > MAX_RECT_WIDTH || (decorativo.alto || 0) > MAX_RECT_HEIGHT)
          ) {
            return;
          }

          const w = decorativo.ancho ?? 0;
          const h = decorativo.alto ?? 0;

          expandBox(
            decorativo.coordX,
            decorativo.coordY,
            decorativo.coordX + w,
            decorativo.coordY + h
          );

          if (
            decorativo.tipo === 'linea' &&
            decorativo.punto_final_x != null &&
            decorativo.punto_final_y != null
          ) {
            expandBox(
              decorativo.punto_final_x,
              decorativo.punto_final_y,
              decorativo.punto_final_x,
              decorativo.punto_final_y
            );
          }
        });
      }
    }

    // si no hay nada
    if (!hasAnything) {
      return {
        minX: 0,
        minY: 0,
        maxX: CANVAS_WIDTH,
        maxY: CANVAS_HEIGHT,
        centerX: CANVAS_WIDTH / 2,
        centerY: CANVAS_HEIGHT / 2
      };
    }

    // padding general
    minX -= CANVAS_PADDING;
    minY -= CANVAS_PADDING;
    maxX += CANVAS_PADDING;
    maxY += CANVAS_PADDING;

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    return { minX, minY, maxX, maxY, centerX, centerY };
  };

  const centrarCanvas = () => {
    if (!layout) return;

    const { minX, minY, maxX, maxY, centerX, centerY } = calcularBoundingBox();

    const contentWidth = Math.max(1, maxX - minX);
    const contentHeight = Math.max(1, maxY - minY);

    const scaleX = CANVAS_WIDTH / contentWidth;
    const scaleY = CANVAS_HEIGHT / contentHeight;

    const mesasCount = layout?.mesas?.length ?? 0;

    const MIN_SCALE = 0.40;
    const ZOOM_SOFTENER = 0.92;

    const MAX_SCALE = mesasCount <= 5 ? 0.55 : 1.05;

    let optimalScale = Math.min(scaleX, scaleY, MAX_SCALE);
    optimalScale = Math.max(optimalScale, MIN_SCALE);
    optimalScale *= ZOOM_SOFTENER;

    const scaledCenterX = centerX * optimalScale;
    const scaledCenterY = centerY * optimalScale;

    const offsetX = (CANVAS_WIDTH / 2) - scaledCenterX;
    const offsetY = (CANVAS_HEIGHT / 2) - scaledCenterY;

    setEscala(optimalScale);
    setPosicion({ x: offsetX, y: offsetY });
  };


  useEffect(() => {
    if (open && tarifa) {
      cargarLayout();
      setCantidad(cantidadSeleccionada || 1);

      if (seleccionActual) {
        setAsientosSeleccionados(seleccionActual.asientos || []);

        if (seleccionActual.mesas && typeof seleccionActual.mesas === 'object') {
          setMesasSeleccionadas(seleccionActual.mesas);
        } else {
          setMesasSeleccionadas({});
        }
      } else {
        console.log('No hay seleccionActual');
        setAsientosSeleccionados([]);
        setMesasSeleccionadas({});
      }
    }
  }, [open, tarifa?.id]);

  useEffect(() => {
    if (layout && open) {
      setTimeout(() => {
        centrarCanvas();
      }, 100);
    }
  }, [layout, open]);


  // reset zoom
  useEffect(() => {
    if (!open) {
      setEscala(1);
      setPosicion({ x: 0, y: 0 });
    }
  }, [open]);

  const zoomIn = () => {
    const newScale = Math.min(escala * 1.2, 3);
    setEscala(newScale);
  };

  const zoomOut = () => {
    const newScale = Math.max(escala * 0.8, 0.3);
    setEscala(newScale);
  };

  const resetZoom = () => {
    if (layout) {
      centrarCanvas();
    } else {
      setEscala(1);
      setPosicion({ x: 0, y: 0 });
    }
  };

  const handleDragCanvas = (e) => {
    setPosicion({ x: e.target.x(), y: e.target.y() });
  };

  const incrementarCantidad = () => {
    const limite = tarifa?.limite_persona || 5;
    if (cantidad < limite) {
      const nuevaCantidad = cantidad + 1;
      setCantidad(nuevaCantidad);
    }
  };

  const decrementarCantidad = () => {
    if (cantidad > 1) {
      const nuevaCantidad = cantidad - 1;
      setCantidad(nuevaCantidad);

      if (layout?.tipo_seleccion === 'mesas_seleccion') {
        let entradasActuales = totalEntradasAsignadas;
        let nuevasSelecciones = { ...mesasSeleccionadas };

        const mesasConEntradas = Object.entries(nuevasSelecciones).sort(([, a], [, b]) => b - a);

        while (entradasActuales > nuevaCantidad && mesasConEntradas.length > 0) {
          for (let [mesaId, count] of mesasConEntradas) {
            if (entradasActuales <= nuevaCantidad) break;

            if (count > 1) {
              nuevasSelecciones[mesaId] = count - 1;
              entradasActuales--;
            } else {
              delete nuevasSelecciones[mesaId];
              entradasActuales--;
              const index = mesasConEntradas.findIndex(([id]) => id === mesaId);
              mesasConEntradas.splice(index, 1);
            }
          }
        }

        setMesasSeleccionadas(nuevasSelecciones);
      } else {
        if (asientosSeleccionados.length > nuevaCantidad) {
          const nuevosAsientos = asientosSeleccionados.slice(0, nuevaCantidad);
          setAsientosSeleccionados(nuevosAsientos);
        }
      }
    }
  };

  const cargarLayout = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.ubicacionesNew.getLayoutPorTarifa(tarifa.id);
      if (response.status === "success") {
        setLayout({
          ...response.data,
          decorativos: response.data.ubicacion?.decorativos || []
        });
      } else {
        setError(response.message || "Error cargando mapa");
      }
    } catch (error) {
      console.error("Error cargando mapa:", error);
      setError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const handleAsientoClick = (asiento) => {
    if (!asiento.disponible) return;

    if (asientosSeleccionados.includes(asiento.id)) {
      setAsientosSeleccionados(prev => prev.filter(id => id !== asiento.id));
    } else {
      if (layout?.tipo_seleccion === 'mesas_seleccion') {
        if (asientosSeleccionados.length < cantidad) {
          setAsientosSeleccionados(prev => [...prev, asiento.id]);
        }
      } else {
        setAsientosSeleccionados(prev => [...prev, asiento.id]);
      }
    }
  };

  const handleMesaClick = (mesa, event) => {
    const esEventoVIP = [/* 395, */ 489].includes(tarifa.idevento);

    if (esEventoVIP) {
      setCantidadAdultos(4);
      setCantidadMenoresPagados(0);
      setPasoActual(1);
    }

    if (!esEventoVIP && esTarifaMesaVIP(tarifa, layout)) {
      const totalMesasSeleccionadas = Object.keys(mesasSeleccionadas).length;
      if (totalMesasSeleccionadas >= 1 && !mesasSeleccionadas[mesa.id]) {
        return;
      }
    }

    if (layout?.tipo_seleccion === 'mesas_seleccion' && !mesa.esNumerada && mesa.mesaDisponible) {
      const stage = stageRef.current;
      const stageBox = stage.container().getBoundingClientRect();

      const mesaCentroX = (mesa.coordX + mesa.ancho / 2) * escala + posicion.x;
      const mesaCentroY = (mesa.coordY + mesa.alto / 2) * escala + posicion.y;

      const popoverX = stageBox.left + mesaCentroX;
      const popoverY = stageBox.top + mesaCentroY;

      setMesaActiva(mesa);
      setPopoverPosition({ x: popoverX, y: popoverY });
      setShowPopover(true);
    }
  };

  const renderAsientoMesa = (asiento, x, y) => {
    const isSelected = asientosSeleccionados.includes(asiento.id);
    const isDisponible = !asiento.ocupado && asiento.activo;

    let asientoLabel = asiento.asiento || `${asiento.id}`;

    const formatoLetraNumeros = asientoLabel.match(/^[A-Za-z]+(\d+)$/);
    if (formatoLetraNumeros) {
      asientoLabel = formatoLetraNumeros[1];
    }
    else {
      const match = asientoLabel.match(/-(\d+)$/);
      if (match) {
        asientoLabel = match[1];
      }
    }

    let fillColor = '#E8E8E8';
    let strokeColor = '#5C636A';

    if (isDisponible) {
      fillColor = isSelected ? '#ceceff' : '#c5eeb2';
      strokeColor = isSelected ? '#5656ff' : '#52c41a';
    }

    const radius = isMobile ? 18 : 22;

    return (
      <Group key={`asiento-mesa-${asiento.id}`}>
        <Circle
          x={x}
          y={y}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={1}
          onClick={() => isDisponible && handleAsientoClick({ ...asiento, disponible: isDisponible })}
          onTap={() => isDisponible && handleAsientoClick({ ...asiento, disponible: isDisponible })}
          style={{ cursor: isDisponible ? 'pointer' : 'not-allowed' }}
        />
        <Text
          x={x - (isMobile ? 9 : 11)}
          y={y - (isMobile ? 8 : 9)}
          width={isMobile ? 18 : 22}
          height={isMobile ? 16 : 18}
          align="center"
          verticalAlign="middle"
          text={getNumeroAsiento(asiento)}
          fontSize={isMobile ? 12 : 14}
          fill={
            asiento.ocupado ? "#5C636A" :
              isSelected ? "#5656FF" :
                asiento.disponible ? "#52c41a" : "white"
          }
          fontFamily="Bricolage Grotesque, sans serif"
          listening={false}
        />
        {isSelected && (
          <Circle
            x={x + (isMobile ? 9 : 11)}
            y={y - (isMobile ? 11 : 13)}
            radius={isMobile ? 4 : 5}
            fill="#5656ff"
            stroke="white"
            strokeWidth={1}
            listening={false}
          />
        )}
      </Group>
    );
  };

  const renderDecorativo = (decorativo) => {
    const { tipo, coordX, coordY, ancho, alto, rotation, color, texto, font_size, punto_final_x, punto_final_y } = decorativo;

    return (
      <Group
        key={`decorativo-${decorativo.id}`}
        x={coordX}
        y={coordY}
        rotation={rotation || 0}
        listening={false}
      >
        {tipo === 'rectangulo' && (
          <Rect
            x={0}
            y={0}
            width={ancho || 150}
            height={alto || 100}
            fill={color || '#FFD700'}
            opacity={0.8}
            listening={false}
          />
        )}

        {tipo === 'circulo' && (
          <Circle
            x={0}
            y={0}
            radius={(ancho || 50) / 2}
            fill={color || '#4CAF50'}
            opacity={0.8}
            listening={false}
          />
        )}

        {tipo === 'linea' && (
          <Line
            points={[
              0,
              0,
              (punto_final_x !== undefined && coordX !== undefined)
                ? punto_final_x - coordX
                : 150,
              (punto_final_y !== undefined && coordY !== undefined)
                ? punto_final_y - coordY
                : 0
            ]}
            stroke={color || '#000000'}
            strokeWidth={3}
            lineCap="round"
            listening={false}
          />
        )}

        {tipo === 'texto' && (
          <Text
            x={0}
            y={0}
            text={texto || 'Texto'}
            fontSize={font_size || 16}
            fontFamily="Bricolage Grotesque, sans-serif"
            fill={color || '#000000'}
            listening={false}
          />
        )}
      </Group>
    );
  };

  const esTarifaMesaVIP = (tarifa, layout) => {
    return (
      layout?.tipo_seleccion === 'mesas_seleccion' &&
      (tarifa?.idubicacion === 530 || tarifa?.idubicacion === 538)
    );
  };

  const ajustarRotacionTexto = (rotacion) => {
    let rot = ((rotacion % 360) + 360) % 360;
    if (rot > 90 && rot < 270) {
      return rot + 180;
    }
    return rot;
  };

  const renderEtiquetasFilas = () => {
    if (!layout?.asientos || parseInt(tarifa?.idevento) !== 493) return [];

    // asientos x fila
    const filas = {};
    layout.asientos.forEach(asiento => {
      if (asiento.fila && asiento.fila.trim() !== '') {
        if (!filas[asiento.fila]) {
          filas[asiento.fila] = [];
        }
        filas[asiento.fila].push(asiento);
      }
    });

    // calcular la posicion de la etiqueta
    return Object.entries(filas).map(([nombreFila, asientosFila]) => {
      if (asientosFila.length === 0) return null;

      // filas duplicadas, agrupar por Y
      const gruposPorY = {};
      const toleranciaY = 30;

      asientosFila.forEach(asiento => {
        let grupoEncontrado = false;
        Object.keys(gruposPorY).forEach(yKey => {
          const yGrupo = parseFloat(yKey);
          if (Math.abs(asiento.coordY - yGrupo) <= toleranciaY) {
            gruposPorY[yKey].push(asiento);
            grupoEncontrado = true;
          }
        });

        if (!grupoEncontrado) {
          gruposPorY[asiento.coordY] = [asiento];
        }
      });

      // la fila mas baja
      const yMasAbajo = Math.max(...Object.keys(gruposPorY).map(y => parseFloat(y)));
      const asientosDelGrupoAbajo = gruposPorY[yMasAbajo];

      // primer asiento
      const primerAsiento = asientosDelGrupoAbajo.reduce((min, asiento) =>
        asiento.coordX < min.coordX ? asiento : min
      );

      const rotacion = primerAsiento.rotation || 0;
      const distancia = isMobile ? 35 : 45;

      // calcular posicion segun la rotacion
      const rad = (rotacion * Math.PI) / 180;
      const anguloEtiqueta = rad + (270 * Math.PI / 180);

      const x = primerAsiento.coordX + Math.cos(anguloEtiqueta) * distancia;
      const y = primerAsiento.coordY + Math.sin(anguloEtiqueta) * distancia;

      // fuente segun el zoom
      const fontSize = isMobile ? 14 : Math.max(14, Math.min(18, 16 * escala));

      const rotacionAjustada = ajustarRotacionTexto(rotacion);

      return (
        <Text
          key={`etiqueta-fila-${nombreFila}`}
          x={x}
          y={y}
          text={nombreFila}
          fontSize={fontSize}
          fill="#333333"
          fontFamily="Bricolage Grotesque, sans-serif"
          fontStyle="bold"
          rotation={rotacionAjustada}
          listening={false}
          align="center"
        />
      );
    }).filter(Boolean);
  };

  const renderAsientoFila = (asiento, x, y) => {
    const isSelected = asientosSeleccionados.includes(asiento.id);
    const isDisponible = !asiento.ocupado && asiento.activo;

    let asientoLabel = String(getNumeroAsiento(asiento));

    const formatoLetraNumeros = asientoLabel.match(/^[A-Za-z]+(\d+)$/);
    if (formatoLetraNumeros) {
      asientoLabel = formatoLetraNumeros[1];
    }
    else {
      const match = asientoLabel.match(/-(\d+)$/);
      if (match) {
        asientoLabel = match[1];
      }
    }

    let fillColor = '#E8E8E8';
    let strokeColor = '#5C636A';

    if (isDisponible) {
      fillColor = isSelected ? '#ceceff' : '#c5eeb2';
      strokeColor = isSelected ? '#5656ff' : '#000000';
    }

    const radius = isMobile ? 22 : 28;

    return (
      <Group key={`asiento-fila-${asiento.id}`}>
        <Circle
          x={asiento.coordX}
          y={asiento.coordY}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={1}
          rotation={asiento.rotation || 0}
          onClick={() => isDisponible && handleAsientoClick({ ...asiento, disponible: isDisponible })}
          onTap={() => isDisponible && handleAsientoClick({ ...asiento, disponible: isDisponible })}
          style={{ cursor: isDisponible ? 'pointer' : 'not-allowed' }}
        />
        <Text
          x={asiento.coordX - (isMobile ? 18 : 22)}
          y={asiento.coordY - (isMobile ? 10 : 12)}
          width={isMobile ? 38 : 46}
          height={isMobile ? 22 : 26}
          align="center"
          verticalAlign="middle"
          text={getNumeroAsiento(asiento)}
          fontSize={isMobile ? 18 : 22}
          fill={
            asiento.ocupado ? "#5C636A" :
              isSelected ? "#5656FF" :
                asiento.disponible ? "#000000" : "white"
          }
          fontStyle="bold"
          fontFamily="Bricolage Grotesque, sans serif"
          listening={false}
        />
        {isSelected && (
          <Circle
            x={asiento.coordX + (isMobile ? 14 : 17)}
            y={asiento.coordY - (isMobile ? 14 : 17)}
            radius={isMobile ? 4 : 5}
            fill="#5656ff"
            stroke="white"
            strokeWidth={1}
            rotation={asiento.rotation || 0}
            listening={false}
          />
        )}
      </Group>
    );
  };

  const renderMesa = (mesa) => {
    const entradasEnEstaMesa = mesasSeleccionadas[mesa.id] || 0;
    const tienentradas = entradasEnEstaMesa > 0;
    const centroX = mesa.coordX + mesa.ancho / 2;
    const centroY = mesa.coordY + mesa.alto / 2;

    const asientosVisibles = Array.isArray(mesa.asientos)
      ? mesa.asientos.filter(a => a.visible_cliente)
      : [];

    const esNumerada = asientosVisibles.length > 0;
    const asientosDisponiblesEnMesa = mesa.asientos_disponibles || 0;
    const mesaDisponible = asientosDisponiblesEnMesa > 0;

    const esMesaActiva = mesaActiva?.id === mesa.id;

    // si es seleccionada, un poquito mas grande
    const radioBase = Math.min(mesa.ancho, mesa.alto) / 2 * 1.2;
    const radioFinal = esMesaActiva ? radioBase * 1.15 : radioBase;

    const mesaShape = (
      <Circle
        x={centroX}
        y={centroY}
        radius={radioFinal}
        rotation={mesa.rotation || 0}
        fill={
          !mesaDisponible ? "#E8E8E8" :
            tienentradas ? "#ceceff" :
              "#c5eeb2"
        }
        stroke={
          !mesaDisponible ? "#5C636A" :
            tienentradas ? "#5656ff" :
              "#000000"
        }
        strokeWidth={esMesaActiva ? 3 : (tienentradas ? 2 : 1)}
        onClick={(e) => {
          if (!esNumerada && layout?.tipo_seleccion === 'mesas_seleccion' && mesaDisponible) {
            const konvaEvent = e.evt;
            handleMesaClick({ ...mesa, esNumerada, mesaDisponible }, konvaEvent);
          }
        }}
        onTap={(e) => {
          if (!esNumerada && layout?.tipo_seleccion === 'mesas_seleccion' && mesaDisponible) {
            const konvaEvent = e.evt;
            handleMesaClick({ ...mesa, esNumerada, mesaDisponible }, konvaEvent);
          }
        }}
        style={{
          cursor: (!esNumerada && layout?.tipo_seleccion === 'mesas_seleccion' && mesaDisponible) ? 'pointer' :
            !mesaDisponible ? 'not-allowed' : 'default'
        }}
      />
    );

    const mesasOrdenadas = layout?.mesas || [];
    const mesaIndex = mesasOrdenadas.findIndex(m => m.id === mesa.id) + 1;

    return (
      <Group key={`mesa-${mesa.id}`}>
        {mesaShape}

        <Text
          x={centroX}
          y={centroY - (isMobile ? 18 : 22)}
          text={
            mesa.mesa
              ? mesa.mesa.replace(/^Mesa\s*/i, '')
              : mesaIndex.toString()
          }
          fontSize={isMobile ? 24 : 28}
          fill={
            !mesaDisponible ? "#5C636A" :
              tienentradas ? "#5656ff" :
                "#000000"
          }
          fontStyle="bold"
          fontFamily="Bricolage Grotesque, sans serif"
          listening={false}
          align="center"
          //width={isMobile ? 24 : 32}
          offsetX={isMobile ? 12 : 16}
        />

        {/* <Text
          x={centroX}
          y={centroY + (isMobile ? 8 : 12)}
          text={!mesaDisponible ? "Ocupado" : 'Libre'}
          fontSize={isMobile ? 16 : 18}
          fill={
            !mesaDisponible ? "#5C636A" :
              tienentradas ? "#5656ff" :
                "#000000"
          }
          fontFamily="Bricolage Grotesque, sans serif"
          listening={false}
          align="center"
          width={isMobile ? 40 : 50}
          offsetX={isMobile ? 20 : 25}
        /> */}

        {tienentradas && !esMesaActiva && (
          <>
            <Circle
              x={centroX + radioFinal * 0.6}
              y={centroY - radioFinal * 0.6}
              radius={isMobile ? 10 : 12}
              fill="#5656ff"
              stroke="#ffffff"
              strokeWidth={1}
              listening={false}
            />
            {<Text
              x={centroX + radioFinal * 0.6}
              y={centroY - radioFinal * 0.6 - (isMobile ? 5 : 6)}
              text={entradasEnEstaMesa.toString()}
              fontSize={isMobile ? 9 : 12}
              fill="white"
              fontStyle="bold"
              fontFamily="Bricolage Grotesque, sans serif"
              listening={false}
              align="center"
              offsetX={isMobile ? 4 : 6}
            />}
          </>
        )}

        {layout?.tipo_seleccion === 'mesas_numeradas' &&
          esNumerada &&
          asientosVisibles.map((asiento, index) => {
            const radio = Math.min(mesa.ancho, mesa.alto) / 2 + (isMobile ? 20 : 25);
            const angulo = (2 * Math.PI * index) / asientosVisibles.length;
            const asientoX = centroX + radio * Math.cos(angulo);
            const asientoY = centroY + radio * Math.sin(angulo);

            const asientoConCoords = {
              ...asiento,
              coordX: asientoX,
              coordY: asientoY
            };

            return renderAsientoMesa(asientoConCoords, asientoX, asientoY);
          })}
      </Group>
    );
  };

  const renderAsientosIndividuales = () => {
    return layout?.asientos?.map(asiento => {
      if (asiento.fila && asiento.fila.trim() !== '') {
        return renderAsientoFila(asiento);
      } else {
        return renderAsientoFila(asiento);
      }
    });
  };

  const [tarifaEntradaSeleccionada, setTarifaEntradaSeleccionada] = useState(null);
  const [cantidadEntradasSeleccionadas, setCantidadEntradasSeleccionadas] = useState(0);

  const [cantidadAdultos, setCantidadAdultos] = useState(4);
  const [cantidadMenoresPagados, setCantidadMenoresPagados] = useState(0);
  const [pasoActual, setPasoActual] = useState(1);

  const TARIFA_MENOR_MESA_ID = 702;

  const TARIFAS_EVENTOS = {
    /* 395: {
      menores: 702
    }, */
    489: {
      viernes: {
        general: 714,
        menores: 703,
        mesa: 705
      },
      sabado: {
        general: 715,
        menores: 704,
        mesa: 706
      }
    }
  };

  const obtenerTarifaMenores = () => {
    /*  if (tarifa.idevento === 395) {
       return TARIFAS_EVENTOS[395].menores;
     } */

    if (tarifa.idevento === 489) {
      // viernes o sabado segun la tarifa actual
      if (tarifa.id === TARIFAS_EVENTOS[489].viernes.mesa) {
        return TARIFAS_EVENTOS[489].viernes.menores;
      } else if (tarifa.id === TARIFAS_EVENTOS[489].sabado.mesa) {
        return TARIFAS_EVENTOS[489].sabado.menores;
      }
    }

    return TARIFA_MENOR_MESA_ID;
  };

  const obtenerTarifasEntradasPorDia = () => {
    const todasLasEntradas = tarifasTicketsFiltrados?.filter(t =>
      t.ubicacion &&
      t.ubicacion.tipo_ubicacion !== 2 &&
      t.ubicacion.tipo_ubicacion !== 3 &&
      t.disponible
    ) || [];

    // si no es el 489 traer todas las tarifas
    if (tarifa.idevento !== 489) {
      return todasLasEntradas;
    }

    // para moussy, traer o viernes o sabado
    if (tarifa.id === TARIFAS_EVENTOS[489].viernes.mesa) {
      return todasLasEntradas.filter(t => t.id === TARIFAS_EVENTOS[489].viernes.general);
    } else if (tarifa.id === TARIFAS_EVENTOS[489].sabado.mesa) {
      return todasLasEntradas.filter(t => t.id === TARIFAS_EVENTOS[489].sabado.general);
    }

    return todasLasEntradas;
  };

  const confirmarSeleccion = () => {
    if (layout?.tipo_seleccion === 'general') {
      onConfirmar({ cantidad, asientos: [], mesas: {} });
      onClose();
      return;
    }

    if (layout?.tipo_seleccion === 'mesas_seleccion') {

      // MESAS JAUKANIGAAS
      if (esTarifaMesaVIP(tarifa, layout) && !tarifa.preimpreso) {
        onConfirmar({
          cantidad: totalEntradasAsignadas,
          asientos: [],
          mesas: mesasSeleccionadas
        });
        onClose();
        return;
      }

      const asientosParaEnviar = convertirMesasAAsientos();
      onConfirmar({
        cantidad: totalEntradasAsignadas,
        asientos: asientosParaEnviar,
        mesas: mesasSeleccionadas
      });
      onClose();
      return;
    }

    if (layout?.tipo_seleccion === 'mesas_numeradas') {
      onConfirmar({
        cantidad: asientosSeleccionados.length,
        asientos: asientosSeleccionados,
        mesas: {}
      });
      onClose();
      return;
    }

    onConfirmar({
      cantidad: asientosSeleccionados.length,
      asientos: asientosSeleccionados,
      mesas: {}
    });
    onClose();
  };

  const obtenerTarifasEntradas = () => {
    return obtenerTarifasEntradasPorDia();
  };

  const convertirMesasAAsientos = () => {
    const asientosParaEnviar = [];

    Object.entries(mesasSeleccionadas).forEach(([mesaId, cantidadEnMesa]) => {
      const mesa = layout.mesas.find(m => m.id == mesaId);
      if (mesa && mesa.asientos) {
        const asientosDisponibles = mesa.asientos
          .filter(a => !a.ocupado && a.activo)
          .slice(0, cantidadEnMesa)
          .map(a => a.id);

        asientosParaEnviar.push(...asientosDisponibles);
      }
    });

    return asientosParaEnviar;
  };

  const confirmarMesaConEntradas = (conEntradas) => {
    const todosLosAsientos = mesaActiva.asientos.map(a => a.id);

    const seleccionMesa = {
      cantidad: 1,
      asientos: todosLosAsientos,
      mesas: { [mesaActiva.id]: 1 },
      metadata: {
        adultos: cantidadAdultos,
        menoresPagados: cantidadMenoresPagados,
        precioMesa: calcularPrecioMesa()
      }
    };

    const datosMenores = cantidadMenoresPagados > 0 ? {
      tarifaId: obtenerTarifaMenores(),
      cantidad: cantidadMenoresPagados
    } : null;

    const datosEntradas = (conEntradas && tarifaEntradaSeleccionada && cantidadEntradasSeleccionadas > 0) ? {
      agregarEntradas: true,
      tarifaId: tarifaEntradaSeleccionada.id,
      cantidad: cantidadEntradasSeleccionadas
    } : { agregarEntradas: false };

    onConfirmar(seleccionMesa, datosEntradas, datosMenores);

    setShowPopover(false);
    setMesaActiva(null);
    setPasoActual(1);
    setTarifaEntradaSeleccionada(null);
    setCantidadEntradasSeleccionadas(0);
    onClose();
  };

  const calcularPrecioMesa = () => {
    // moussy
    if (tarifa.idevento === 489) {
      const precioBase = cantidadAdultos === 4 ? 240000 : 350000;
      return precioBase;
    }

    // 395
    /* const precioBase = cantidadAdultos === 4 ? 230 : 250;
    return precioBase; */
  };

  const calcularPrecioVisual = () => {
    if (tarifa.idevento === 489) {
      const precioBase = cantidadAdultos === 4 ? 240000 : 350000;
      const precioMenores = cantidadMenoresPagados * 30000;
      return precioBase + precioMenores;
    }

    const precioBase = cantidadAdultos === 4 ? 230 : 250;
    const precioMenores = cantidadMenoresPagados * 300;
    return precioBase + precioMenores;
  };

  const getLimites = () => {
    if (cantidadAdultos === 4) {
      return { maxMenores: 1, limiteTotal: 5 };
    } else {
      return { maxMenores: 1, limiteTotal: 7 };
    }
  };

  const CircleIcon = ({ color }) => (
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: color,
        border: '1px solid #999'
      }}
    />
  );

  const puedeConfirmar = () => {
    if (loading || error) return false;

    switch (layout?.tipo_seleccion) {
      case 'general':
        return true;
      case 'mesas_seleccion':
        return totalEntradasAsignadas > 0;
      case 'mesas_numeradas':
      default:
        return asientosSeleccionados.length > 0;
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    onClose();
  };

  if (!tarifa || !open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? '16px 16px 0 0' : '16px',
          boxShadow: '0 8px 32px rgba(44, 62, 80, 0.12)',
          bgcolor: '#ffff',
          height: isMobile ? '95vh' : '85vh',
          ...(isMobile && {
            position: 'fixed',
            bottom: 0,
            top: 'auto',
            left: 0,
            right: 0,
            transform: 'none',
            margin: 0,
            width: '100%'
          })
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1,
        fontWeight: 700,
        fontSize: isMobile ? '20px' : '24px',
        color: '#0b0060',
        borderBottom: '1px solid #E4E4E7',
        mb: 2,
        padding: "8px 24px",
      }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="h6" sx={{
            fontWeight: 700,
            fontSize: isMobile ? '16px' : '22px',
            color: '#0b0060'
          }}>
            {tarifa.concepto}
          </Typography>
          {isMobile && (
            <Box sx={{
              left: 0,
              right: 0,
              display: 'flex',
              gap: 1,
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Chip
                label="Disponible"
                size="small"
                sx={{
                  bgcolor: '#c5eeb2',
                  color: '#52c41a',
                  fontWeight: 500,
                  fontSize: '11px'
                }}
              />
              <Chip
                label="Seleccionado"
                size="small"
                sx={{
                  bgcolor: '#ceceff',
                  color: '#5656ff',
                  fontWeight: 500,
                  fontSize: '11px'
                }}
              />
              <Chip
                label="Ocupado"
                size="small"
                sx={{
                  bgcolor: '#E8E8E8',
                  color: '#5C636A',
                  fontWeight: 500,
                  fontSize: '11px'
                }}
              />
            </Box>
          )}
        </Box>
        <Button
          onClick={onClose}
          size="small"
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseOutlined />
        </Button>
      </DialogTitle>

      <DialogContent sx={{
        pt: isMobile ? 1 : 3,
        position: 'relative',
        overflow: 'hidden',
        px: isMobile ? 1 : 3,
        height: isMobile ?
          (isSmallScreen ? 'calc(95vh - 140px)' : 'calc(95vh - 120px)') :
          'calc(85vh - 120px)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
            <Typography sx={{ ml: 2 }}>Cargando mapa...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{
            display: 'flex',
            gap: isMobile ? 0 : 4,
            alignItems: 'flex-start',
            minHeight: 0,
            height: "100%",
            flexDirection: isMobile ? 'column' : 'row',
            overflow: 'hidden'
          }}>
            {/* Panel izquierdo - desktop */}
            {!isMobile && (
              <Box sx={{
                width: 320,
                bgcolor: '#F9FAFB',
                borderRadius: '10px',
                p: 3,
                boxShadow: '0 1px 3px rgba(44,62,80,0.07)',
                border: '1px solid #e5e7eb',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                height: "100%",
                flexShrink: 0,
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>

                {/* Leyenda Desktop */}
                <Box sx={{ display: 'flex', flexDirection: "column", gap: 2, mb: 2 }}>
                  <Typography sx={{
                    fontWeight: 700,
                    fontSize: '16px',
                    color: '#5656ff',
                  }}>Leyenda</Typography>
                  <Chip
                    icon={<CircleIcon color="#52C41A" />}
                    label="Disponible"
                    size="small"
                    sx={{ bgcolor: "#C5EBB2", color: "#52C41A", fontWeight: 600 }}
                  />
                  <Chip
                    icon={<CircleIcon color="#5656ff" />}
                    label="Seleccionado"
                    size="small"
                    sx={{ bgcolor: "#ceceff", color: "#5656ff", fontWeight: 600 }}
                  />
                  <Chip
                    icon={<CircleIcon color="#5C636A" />}
                    label="Ocupado"
                    size="small"
                    sx={{ bgcolor: "#E8E8E8", color: "#5C636A", fontWeight: 600 }}
                  />
                </Box>

                <Box sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: 'white',
                  borderRadius: '8px',
                  p: 1,
                  border: '1px solid #e5e7eb'
                }}>
                  <IconButton size="small" onClick={zoomOut} sx={{ width: 32, height: 32 }}>
                    <ZoomOut fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ mx: 1, minWidth: '60px', textAlign: 'center', fontSize: '12px' }}>
                    {Math.round(escala * 100)}%
                  </Typography>
                  <IconButton size="small" onClick={zoomIn} sx={{ width: 32, height: 32 }}>
                    <ZoomIn fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={resetZoom} sx={{ ml: 0.5, width: 32, height: 32 }}>
                    <RestartAlt fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Canvas */}
            <Box sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              width: '100%',
              maxWidth: isMobile ? '100%' : '700px',
              overflow: 'hidden',
              mx: 'auto'
            }}>
              {isMobile && (
                <Box sx={{
                  position: 'absolute',
                  top: -50,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2
                }}>
                  <ButtonGroup variant="outlined" size="small">
                    <Button
                      onClick={decrementarCantidad}
                      disabled={cantidad <= 1}
                      sx={{ color: "#0b0060", borderColor: "#0b0060", fontSize: '12px' }}
                    >
                      -
                    </Button>
                    <Button
                      disabled
                      sx={{
                        color: "#0b0060",
                        borderColor: "#0b0060",
                        '&.Mui-disabled': { color: "#0b0060" },
                        fontSize: '12px'
                      }}
                    >
                      {cantidad}
                    </Button>
                    <Button
                      onClick={incrementarCantidad}
                      sx={{ color: "#0b0060", borderColor: "#0b0060", fontSize: '12px' }}
                    >
                      +
                    </Button>
                  </ButtonGroup>

                  <Typography variant="caption" sx={{ color: "#5656ff", fontWeight: 600 }}>
                    {layout?.tipo_seleccion === 'mesas_seleccion'
                      ? `${totalEntradasAsignadas}`
                      : `${asientosSeleccionados.length}/${cantidad}`}
                  </Typography>
                </Box>
              )}

              <Paper elevation={0} sx={{
                bgcolor: '#f9fafb',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                width: '100%',
                maxWidth: CANVAS_WIDTH,
                height: CANVAS_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                position: 'relative',
                overflow: 'hidden'
              }}
                data-canvas-container="true">
                <Stage
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                  ref={stageRef}
                  scaleX={escala}
                  scaleY={escala}
                  x={posicion.x}
                  y={posicion.y}
                  draggable
                  onDragEnd={handleDragCanvas}
                  style={{
                    background: 'transparent',
                    display: 'block',
                    cursor: escala > 1 ? 'grab' : 'default',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    touchAction: 'none'
                  }}
                  onMouseDown={(e) => {
                    if (escala > 1) {
                      e.target.getStage().container().style.cursor = 'grabbing';
                    }
                  }}
                  onMouseUp={(e) => {
                    if (escala > 1) e.target.getStage().container().style.cursor = 'grab';
                  }}
                >
                  <Layer>
                    {layout?.decorativos
                      ?.slice()
                      .sort((a, b) => (a.z_index || 1) - (b.z_index || 1))
                      .map(decorativo => renderDecorativo(decorativo))}
                    {layout?.mesas?.map(mesa => renderMesa(mesa))}
                    {renderAsientosIndividuales()}
                    {renderEtiquetasFilas()}
                  </Layer>
                </Stage>

                {showPopover && mesaActiva && (
                  <>
                    <Box
                      sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1400,
                        backgroundColor: 'transparent'
                      }}
                      onClick={() => {
                        setShowPopover(false);
                        setMesaActiva(null);
                        setPasoActual(1);
                      }}
                    />

                    <Paper
                      elevation={8}
                      sx={{
                        position: 'fixed',
                        left: isMobile
                          ? '50%'
                          : ([/* 395, */ 489].includes(tarifa.idevento)
                            ? Math.min(popoverPosition.x - 130, window.innerWidth - 280)
                            : Math.min(popoverPosition.x - 80, window.innerWidth - 180)),
                        top: isMobile
                          ? '50%'
                          : ([/* 395, */ 489].includes(tarifa.idevento)
                            ? Math.max(popoverPosition.y - 200, 20)
                            : Math.max(popoverPosition.y - 120, 20)),
                        transform: isMobile ? 'translate(-50%, -50%)' : 'none',
                        zIndex: 1500,
                        bgcolor: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        width: isMobile
                          ? '90%'
                          : ([/* 395, */ 489].includes(tarifa.idevento) ? 260 : 160),
                        maxWidth: isMobile
                          ? 340
                          : ([/* 395, */ 489].includes(tarifa.idevento) ? 260 : 'none'),
                        p: 2.5,
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                      }}
                    >
                      {/* POPOVER CON PASOS*/}
                      {[/* 395, */ 489].includes(tarifa.idevento) ? (
                        <>
                          {pasoActual === 1 && (
                            <>
                              <Box sx={{ mb: 2.5 }}>
                                <Typography variant="h6" sx={{
                                  fontWeight: 700,
                                  color: '#0b0060',
                                  fontSize: '16px',
                                  mb: 0.5
                                }}>
                                  Mesa {layout?.mesas?.findIndex(m => m.id === mesaActiva.id) + 1}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#00923B', fontSize: '13px' }}>
                                  Cant. de personas por mesa
                                </Typography>
                              </Box>

                              <Box sx={{ mb: 2 }}>
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 2,
                                  bgcolor: '#F8F9FB',
                                  borderRadius: '10px',
                                  p: 1
                                }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => setCantidadAdultos(4)}
                                    disabled={cantidadAdultos === 4}
                                    sx={{
                                      bgcolor: 'white',
                                      border: '2px solid #e5e7eb',
                                      width: 28,
                                      height: 28,
                                      '&:hover': {
                                        bgcolor: '#f3f4f6',
                                        borderColor: '#5656ff'
                                      },
                                      '&:disabled': {
                                        opacity: 0.4,
                                        cursor: 'not-allowed'
                                      }
                                    }}
                                  >
                                    <Typography sx={{ fontWeight: 700, color: '#5656ff', fontSize: '16px' }}>−</Typography>
                                  </IconButton>

                                  <Typography variant="h3" sx={{
                                    fontWeight: 700,
                                    color: '#0b0060',
                                    fontSize: '32px',
                                    minWidth: 50,
                                    textAlign: 'center'
                                  }}>
                                    {cantidadAdultos}
                                  </Typography>

                                  <IconButton
                                    size="small"
                                    onClick={() => setCantidadAdultos(6)}
                                    disabled={cantidadAdultos === 6}
                                    sx={{
                                      bgcolor: 'white',
                                      border: '2px solid #e5e7eb',
                                      width: 28,
                                      height: 28,
                                      '&:hover': {
                                        bgcolor: '#f3f4f6',
                                        borderColor: '#5656ff'
                                      },
                                      '&:disabled': {
                                        opacity: 0.4,
                                        cursor: 'not-allowed'
                                      }
                                    }}
                                  >
                                    <Typography sx={{ fontWeight: 700, color: '#5656ff', fontSize: '16px' }}>+</Typography>
                                  </IconButton>
                                </Box>
                              </Box>

                              <Alert
                                severity="info"
                                icon={false}
                                sx={{
                                  mb: 2,
                                  fontSize: '11px',
                                  bgcolor: 'transparent',
                                  color: '#A40101',
                                  border: 'none',
                                  py: 1,
                                  px: 1.5,
                                  '& .MuiAlert-message': {
                                    width: '100%',
                                    padding: 0
                                  }
                                }}
                              >
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, mb: 0.3 }}>
                                  Con tu mesa VIP ya tenés incluida 1 silla para menor.
                                </Typography>
                                <Typography sx={{ fontSize: '10px', lineHeight: 1.4 }}>
                                  {cantidadMenoresPagados === 0 && "Si necesitás otra, podés sumarla en este paso."}
                                </Typography>
                              </Alert>

                              {/* Sillas menores */}
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1, color: '#00923B', fontSize: '13px' }}>
                                  Silla para Menor (+$30.000)
                                </Typography>
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 2,
                                  bgcolor: '#F8F9FB',
                                  borderRadius: '10px',
                                  p: 1
                                }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => setCantidadMenoresPagados(Math.max(0, cantidadMenoresPagados - 1))}
                                    disabled={cantidadMenoresPagados === 0}
                                    sx={{
                                      bgcolor: 'white',
                                      border: '2px solid #e5e7eb',
                                      width: 28,
                                      height: 28,
                                      '&:hover': {
                                        bgcolor: '#f3f4f6',
                                        borderColor: '#5656ff'
                                      },
                                      '&:disabled': {
                                        opacity: 0.4,
                                        cursor: 'not-allowed'
                                      }
                                    }}
                                  >
                                    <Typography sx={{ fontWeight: 700, color: '#5656ff', fontSize: '16px' }}>−</Typography>
                                  </IconButton>

                                  <Typography variant="h3" sx={{
                                    fontWeight: 700,
                                    color: '#0b0060',
                                    fontSize: '36px',
                                    minWidth: 50,
                                    textAlign: 'center'
                                  }}>
                                    {cantidadMenoresPagados}
                                  </Typography>

                                  <IconButton
                                    size="small"
                                    onClick={() => setCantidadMenoresPagados(Math.min(getLimites().maxMenores, cantidadMenoresPagados + 1))}
                                    disabled={cantidadMenoresPagados >= getLimites().maxMenores}
                                    sx={{
                                      bgcolor: 'white',
                                      border: '2px solid #e5e7eb',
                                      width: 28,
                                      height: 28,
                                      '&:hover': {
                                        bgcolor: '#f3f4f6',
                                        borderColor: '#5656ff'
                                      },
                                      '&:disabled': {
                                        opacity: 0.4,
                                        cursor: 'not-allowed'
                                      }
                                    }}
                                  >
                                    <Typography sx={{ fontWeight: 700, color: '#5656ff', fontSize: '16px' }}>+</Typography>
                                  </IconButton>
                                </Box>
                              </Box>

                              {/* Total */}
                              <Box sx={{
                                bgcolor: '#f0f0ff',
                                p: 1,
                                borderRadius: '10px',
                                mb: 1.5,
                                border: '2px solid #ceceff',
                                flexWrap: "wrap",
                              }}>
                                <Typography variant="h6" sx={{
                                  color: '#5656ff',
                                  fontWeight: 700,
                                  textAlign: 'center',
                                  fontSize: '16px',

                                }}>
                                  Total: ${calcularPrecioVisual().toLocaleString()}
                                </Typography>
                              </Box>

                              <Button
                                variant="contained"
                                fullWidth
                                onClick={() => setPasoActual(2)}
                                sx={{
                                  bgcolor: '#5656ff',
                                  color: 'white',
                                  fontWeight: 600,
                                  py: 1.2,
                                  fontSize: '14px',
                                  borderRadius: '8px',
                                  textTransform: 'none',
                                  '&:hover': { bgcolor: '#4545CC' }
                                }}
                              >
                                Confirmar
                              </Button>
                            </>
                          )}

                          {/* PASO 2 */}
                          {pasoActual === 2 && (
                            <>
                              {/* Header */}
                              <Box sx={{ gap: 2 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => setPasoActual(1)}
                                  sx={{ mb: 0.5 }}
                                >
                                  <Typography sx={{ fontSize: '18px' }}>←</Typography>
                                </IconButton>
                                <Alert
                                  severity="info"
                                  icon={false}
                                  sx={{
                                    mb: 2,
                                    fontSize: '12px',
                                    bgcolor: 'transparent',
                                    color: '#A40101',
                                    border: 'none',
                                    py: 1,
                                    px: 1.5,
                                    '& .MuiAlert-message': {
                                      width: '100%',
                                      padding: 0
                                    }
                                  }}
                                >
                                  <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.2 }}>
                                    La compra de una mesa VIP no incluye el acceso al predio.
                                  </Typography>
                                  <Typography sx={{ fontSize: '12px', fontWeight: 600, lineHeight: 1.4 }}>
                                    Si aún no contás con tu entrada general, este es el momento de adquirirla.
                                  </Typography>
                                </Alert>

                                <Alert
                                  severity="info"
                                  icon={false}
                                  sx={{
                                    mb: 2,
                                    fontSize: '12px',
                                    bgcolor: 'transparent',
                                    color: '#A40101',
                                    border: 'none',
                                    py: 1,
                                    px: 1.5,
                                    '& .MuiAlert-message': {
                                      width: '100%',
                                      padding: 0
                                    }
                                  }}
                                >
                                  <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.2 }}>
                                    Menores de 10 años no pagan ingreso al predio.
                                  </Typography>
                                </Alert>

                                <Typography variant="h6" sx={{
                                  fontWeight: 600,
                                  color: '#0b0060',
                                  fontSize: '16px'
                                }}>
                                  Entradas
                                </Typography>
                              </Box>

                              {obtenerTarifasEntradas().length > 0 ? (
                                <Box sx={{ mb: 2 }}>
                                  {obtenerTarifasEntradas().map(tarifaEntrada => {
                                    const cantidadSeleccionada = tarifaEntradaSeleccionada?.id === tarifaEntrada.id ? cantidadEntradasSeleccionadas : 0;
                                    const maxEntradas = cantidadAdultos + cantidadMenoresPagados;

                                    return (
                                      <Box key={tarifaEntrada.id} sx={{ mb: 2 }}>
                                        <Box sx={{ mb: 1, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                                          <Typography variant="body2" sx={{
                                            fontWeight: 600,
                                            color: '#00923B',
                                            fontSize: '13px',
                                          }}>
                                            {tarifaEntrada.concepto}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: '#0b0062', fontSize: '11px' }}>
                                            ${tarifaEntrada.precio.toLocaleString()}
                                          </Typography>
                                        </Box>

                                        <Box sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: 2,
                                          bgcolor: '#F8F9FB',
                                          borderRadius: '10px',
                                          p: 1
                                        }}>
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              if (cantidadSeleccionada > 0) {
                                                if (cantidadSeleccionada === 1) {
                                                  setTarifaEntradaSeleccionada(null);
                                                  setCantidadEntradasSeleccionadas(0);
                                                } else {
                                                  setCantidadEntradasSeleccionadas(cantidadSeleccionada - 1);
                                                }
                                              }
                                            }}
                                            disabled={cantidadSeleccionada === 0}
                                            sx={{
                                              bgcolor: 'white',
                                              border: '2px solid #e5e7eb',
                                              width: 28,
                                              height: 28,
                                              '&:hover': {
                                                bgcolor: '#f3f4f6',
                                                borderColor: '#5656ff'
                                              },
                                              '&:disabled': {
                                                opacity: 0.4,
                                                cursor: 'not-allowed'
                                              }
                                            }}
                                          >
                                            <Typography sx={{ fontWeight: 700, color: '#5656ff', fontSize: '16px' }}>−</Typography>
                                          </IconButton>

                                          <Typography variant="h3" sx={{
                                            fontWeight: 700,
                                            color: '#0b0060',
                                            fontSize: '32px',
                                            minWidth: 50,
                                            textAlign: 'center'
                                          }}>
                                            {cantidadSeleccionada}
                                          </Typography>

                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              if (!tarifaEntradaSeleccionada) {
                                                setTarifaEntradaSeleccionada(tarifaEntrada);
                                                setCantidadEntradasSeleccionadas(1);
                                              }
                                              else if (tarifaEntradaSeleccionada.id === tarifaEntrada.id) {
                                                setCantidadEntradasSeleccionadas(Math.min(maxEntradas, cantidadSeleccionada + 1));
                                              }
                                              else {
                                                setTarifaEntradaSeleccionada(tarifaEntrada);
                                                setCantidadEntradasSeleccionadas(1);
                                              }
                                            }}
                                            disabled={tarifaEntradaSeleccionada?.id === tarifaEntrada.id && cantidadSeleccionada >= maxEntradas}
                                            sx={{
                                              bgcolor: 'white',
                                              border: '2px solid #e5e7eb',
                                              width: 28,
                                              height: 28,
                                              '&:hover': {
                                                bgcolor: '#f3f4f6',
                                                borderColor: '#5656ff'
                                              },
                                              '&:disabled': {
                                                opacity: 0.4,
                                                cursor: 'not-allowed'
                                              }
                                            }}
                                          >
                                            <Typography sx={{ fontWeight: 700, color: '#5656ff', fontSize: '16px' }}>+</Typography>
                                          </IconButton>
                                        </Box>
                                        {tarifaEntradaSeleccionada?.id === tarifaEntrada.id && cantidadSeleccionada >= maxEntradas && (
                                          <Typography variant="caption" sx={{
                                            display: 'block',
                                            textAlign: 'center',
                                            mt: 0.5,
                                            color: '#ef4444',
                                            fontSize: '10px'
                                          }}>
                                            Máximo {maxEntradas} entradas
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ) : (
                                <Alert severity="info" sx={{ mb: 2, fontSize: '11px' }}>
                                  No hay entradas disponibles en este momento.
                                </Alert>
                              )}

                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  onClick={() => {
                                    const conEntradas = tarifaEntradaSeleccionada && cantidadEntradasSeleccionadas > 0;
                                    confirmarMesaConEntradas(conEntradas);
                                  }}
                                  sx={{
                                    bgcolor: '#5656ff',
                                    color: 'white',
                                    fontWeight: 600,
                                    py: 1.2,
                                    fontSize: '14px',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: '#4545CC' }
                                  }}
                                >
                                  Confirmar
                                </Button>
                              </Box>
                            </>
                          )}
                        </>
                      ) : (
                        /* OTROS EVENTOS */
                        <>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{
                              fontWeight: 600,
                              color: '#0b0060',
                              fontSize: '14px'
                            }}>
                              Mesa {mesaActiva?.mesa ? mesaActiva.mesa.replace(/^Mesa\s*/i, '') : (layout?.mesas?.findIndex(m => m.id === mesaActiva.id) + 1)}
                            </Typography>
                            <Typography variant="caption" sx={{
                              color: '#16a34a',
                              fontSize: '10px',
                              display: 'block',
                              fontWeight: 500
                            }}>
                              Valor unitario: ${tarifa.precio.toLocaleString()}
                            </Typography>
                          </Box>

                          {/* Controles de cantidad */}
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: '#f8f9fa',
                            borderRadius: '8px',
                            p: 1
                          }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const entradasActuales = mesasSeleccionadas[mesaActiva.id] || 0;
                                if (esTarifaMesaVIP(tarifa, layout)) {
                                  if (entradasActuales === 1) {
                                    const nuevaSeleccion = { ...mesasSeleccionadas };
                                    delete nuevaSeleccion[mesaActiva.id];
                                    setMesasSeleccionadas(nuevaSeleccion);
                                  }
                                } else {
                                  if (entradasActuales === 1) {
                                    const nuevaSeleccion = { ...mesasSeleccionadas };
                                    delete nuevaSeleccion[mesaActiva.id];
                                    setMesasSeleccionadas(nuevaSeleccion);
                                  } else if (entradasActuales > 1) {
                                    setMesasSeleccionadas(prev => ({
                                      ...prev,
                                      [mesaActiva.id]: prev[mesaActiva.id] - 1
                                    }));
                                  }
                                }
                              }}
                              disabled={!mesasSeleccionadas[mesaActiva.id]}
                              sx={{
                                bgcolor: 'white',
                                border: '1px solid #e5e7eb',
                                width: 32,
                                height: 32,
                                '&:hover': { bgcolor: '#f3f4f6' },
                                '&:disabled': { opacity: 0.4, cursor: 'not-allowed' }
                              }}
                            >
                              <Typography sx={{ fontWeight: 600, color: '#ef4444' }}>−</Typography>
                            </IconButton>

                            <Typography variant="h6" sx={{
                              fontWeight: 600,
                              color: '#0b0060',
                              minWidth: 24,
                              textAlign: 'center'
                            }}>
                              {mesasSeleccionadas[mesaActiva.id] || 0}
                            </Typography>

                            <IconButton
                              size="small"
                              onClick={() => {
                                const entradasActuales = mesasSeleccionadas[mesaActiva.id] || 0;
                                if (esTarifaMesaVIP(tarifa, layout)) {
                                  if (entradasActuales < 1) {
                                    setMesasSeleccionadas(prev => ({
                                      ...prev,
                                      [mesaActiva.id]: 1
                                    }));
                                  }
                                } else {
                                  const asientosDisponibles = mesaActiva.asientos_disponibles || 0;
                                  if (entradasActuales < asientosDisponibles) {
                                    setMesasSeleccionadas(prev => ({
                                      ...prev,
                                      [mesaActiva.id]: (prev[mesaActiva.id] || 0) + 1
                                    }));
                                  }
                                }
                              }}
                              disabled={
                                esTarifaMesaVIP(tarifa, layout)
                                  ? (mesasSeleccionadas[mesaActiva.id] || 0) >= 1
                                  : (mesasSeleccionadas[mesaActiva.id] || 0) >= (mesaActiva.asientos_disponibles || 0)
                              }
                              sx={{
                                bgcolor: 'white',
                                border: '1px solid #e5e7eb',
                                width: 32,
                                height: 32,
                                '&:hover': { bgcolor: '#f3f4f6' },
                                '&:disabled': { opacity: 0.4, cursor: 'not-allowed' }
                              }}
                            >
                              <Typography sx={{ fontWeight: 600, color: '#10b981' }}>+</Typography>
                            </IconButton>
                          </Box>
                        </>
                      )}
                    </Paper>
                  </>
                )}

                {isMobile && (
                  <Box sx={{
                    position: 'absolute',
                    top: isSmallScreen ? 10 : 15,
                    right: isSmallScreen ? 10 : 15,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 0.5,
                    bgcolor: 'rgba(255,255,255,0.95)',
                    borderRadius: '8px',
                    p: 0.5,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    pointerEvents: 'auto',
                    zIndex: 10,
                    height: 32,
                    alignItems: 'center'
                  }}>
                    <IconButton
                      size="small"
                      onClick={zoomOut}
                      sx={{
                        bgcolor: 'white',
                        boxShadow: 1,
                        width: 26,
                        height: 26,
                        minWidth: 26,
                        '&:hover': { bgcolor: '#f5f5f5' },
                        p: 0
                      }}
                    >
                      <ZoomOut fontSize="small" />
                    </IconButton>

                    <Typography variant="caption" sx={{
                      px: 1,
                      fontSize: '9px',
                      lineHeight: 1,
                      color: '#666',
                      minWidth: '25px',
                      textAlign: 'center'
                    }}>
                      {Math.round(escala * 100)}%
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={zoomIn}
                      sx={{
                        bgcolor: 'white',
                        boxShadow: 1,
                        width: 26,
                        height: 26,
                        minWidth: 26,
                        '&:hover': { bgcolor: '#f5f5f5' },
                        p: 0
                      }}
                    >
                      <ZoomIn fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={resetZoom}
                      sx={{
                        bgcolor: 'white',
                        boxShadow: 1,
                        width: 26,
                        height: 26,
                        minWidth: 26,
                        '&:hover': { bgcolor: '#f5f5f5' },
                        p: 0,
                        ml: 0.5
                      }}
                    >
                      <RestartAlt fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        )
        }
      </DialogContent >

      <DialogActions sx={{
        padding: isMobile ? "8px 24px" : "16px 24px",
        justifyContent: 'space-between',
        bgcolor: '#f8f9fa',
        borderTop: '1px solid #e5e7eb',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Typography variant="body1" sx={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: isMobile ? '14px' : '16px'
          }}>
            <ShoppingCart sx={{ fontSize: 16, mr: 0.5 }} />
            Total: ${(() => {
              if ([/* 395, */ 489].includes(tarifa.idevento) && layout?.tipo_seleccion === 'mesas_seleccion') {
                return '0';
              }
              if (layout?.tipo_seleccion === 'mesas_seleccion') {
                return (tarifa.precio * totalEntradasAsignadas).toLocaleString();
              }
              return (tarifa.precio * cantidad).toLocaleString();
            })()}
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: isMobile ? 'column-reverse' : "row",
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? 1 : 2,
          width: isMobile ? '100%' : 'auto',
        }}>
          <Button onClick={onClose} variant="outlined" sx={{
            borderRadius: '10px',
            border: "1px solid #5656FF",
            bgcolor: '#fff',
            color: '#5656FF',
            fontWeight: 600,
            padding: "5px 10px",
            width: isMobile ? '100%' : 'auto',
            '&:hover': { bgcolor: '#f1f5f9' },
            flex: isMobile ? 1 : 'none'
          }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmarSeleccion}
            disabled={!puedeConfirmar()}
            sx={{
              borderRadius: '8px',
              bgcolor: '#5656FF',
              color: '#fff',
              fontWeight: 600,
              padding: "5px 10px",
              width: isMobile ? '100%' : 'auto',
              boxShadow: '0 1px 3px rgba(44,62,80,0.07)',
              '&:hover': { bgcolor: '#1d4ed8' },
              '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
              flex: isMobile ? 1 : 'none'
            }}
          >
            {
              layout?.tipo_seleccion === 'mesas_seleccion' ?
                `Confirmar ${totalEntradasAsignadas} ${totalEntradasAsignadas === 1 ? 'mesa' : 'mesas'}` :
                `Confirmar  ${asientosSeleccionados.length} ${asientosSeleccionados.length === 1 ? 'ticket' : 'tickets'}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog >
  );
};

export default SelectUbicacionModal;